import type {
  ContourId,
  ContourLabelItemId,
} from './contour-definitions';
import type { PolygonClosedContour } from './polygon-closed-contour';
import type { PolygonOpenContour } from './polygon-open-contour';
import type { PointArray } from './types/contour-definition-array-type';

import { useAlert } from '@/src/composables/useAlert';

import {
  ContourBase,
  ContourLabel,
  encodeContourLabelItemId,
} from './contour-definitions';
import { clampContourWithinParent } from './contour-processing/clampContourWithinParent';
import {
  clampOpenEndoWithinEpi,
  isOpenEpiInsideEndo,
} from './contour-processing/clampOpenEndoWithinEpi';
import { createLaxMyocardialPolygon } from './contour-processing/createLaxMyocardialFusionPolygon';
import { epiContainsEndo } from './contour-processing/epiContainsEndo';
import { getPolygonIntersection } from './contour-processing/getPolygonIntersection';
import { isPolygonInside } from './contour-processing/isPolygonInside';
import { removeDuplicatePoints } from './contour-processing/removeDuplicatePoints';
import { removeLoops } from './contour-processing/removeLoops';
import { subtractSiblingOverlaps } from './contour-processing/subtractSiblingOverlaps';

export type ContourRole = 'endo' | 'epi' | 'papillary' | 'exclusion' | null;
export type ContourPlane = 'SAX' | 'LAX' | null;

/**
 * Minimal read surface of a `ContourContainer` required by `ContourRules`.
 * Keeps the rules decoupled from the container implementation.
 */
export interface ContourStore {
  isPresentContourId: (id: ContourId) => boolean;
  isPresentContourLabel: (label: ContourLabel) => boolean;
  getContour: (id: ContourId) => ContourBase | undefined;
  getDrawingContours: () => Record<string, Record<string, ContourBase>>;
}

const ENDO_LABELS: ContourLabel[] = [
  ContourLabel.SaxLvEndo,
  ContourLabel.SaxLvEndoTrabecular,
  ContourLabel.SaxRvEndo,
  ContourLabel.LaxLvEndo,
];

const EPI_LABELS: ContourLabel[] = [
  ContourLabel.SaxLvEpi,
  ContourLabel.SaxLvOpenEpi,
  ContourLabel.LaxLvEpi,
];

const PAPILLARY_LABELS: ContourLabel[] = [
  ContourLabel.SaxLvPapillary,
  ContourLabel.SaxRvPapillary,
];

const EXCLUSION_LABELS: ContourLabel[] = [ContourLabel.SaxLvExclusion];

/**
 * Labels stored as closed polygons. Only these support the turf-based
 * containment / overlap geometry. Open contours (LAX endo/epi, open epi) are
 * intentionally skipped so their existing behaviour is untouched.
 */
const CLOSED_LABELS = new Set<ContourLabel>([
  ContourLabel.SaxLvEndo,
  ContourLabel.SaxLvEndoTrabecular,
  ContourLabel.SaxLvEpi,
  ContourLabel.SaxRvEndo,
  ContourLabel.SaxLvPapillary,
  ContourLabel.SaxRvPapillary,
  ContourLabel.SaxLvExclusion,
]);

export function getContourRole(label: ContourLabel): ContourRole {
  if (ENDO_LABELS.includes(label)) return 'endo';
  if (EPI_LABELS.includes(label)) return 'epi';
  if (
    label === ContourLabel.SaxLvPapillary
    || label === ContourLabel.SaxRvPapillary
  ) {
    return 'papillary';
  }
  if (label === ContourLabel.SaxLvExclusion) return 'exclusion';
  return null;
}

export function getContourPlane(label: ContourLabel): ContourPlane {
  if (label.startsWith('Sax')) return 'SAX';
  if (label.startsWith('Lax')) return 'LAX';
  return null;
}

/** Candidate epi labels (priority order) that should enclose the given endo. */
export function getEpiParentCandidates(endo: ContourLabel): ContourLabel[] {
  if (
    endo === ContourLabel.SaxLvEndo
    || endo === ContourLabel.SaxLvEndoTrabecular
  ) {
    return [ContourLabel.SaxLvEpi, ContourLabel.SaxLvOpenEpi];
  }
  if (endo === ContourLabel.LaxLvEndo) {
    return [ContourLabel.LaxLvEpi];
  }
  return [];
}

/** The single endo a papillary / exclusion contour must remain inside. */
export function getEndoParent(label: ContourLabel): ContourLabel | null {
  if (
    label === ContourLabel.SaxLvPapillary
    || label === ContourLabel.SaxLvExclusion
  ) {
    return ContourLabel.SaxLvEndo;
  }
  if (label === ContourLabel.SaxRvPapillary) {
    return ContourLabel.SaxRvEndo;
  }
  return null;
}

export interface CanAddVerdict {
  ok: boolean;
  reason?: string;
}

/**
 * Rejects incompatible endo/epi additions:
 *  - SAX vs LAX plane mismatch
 *  - two different epi kinds (e.g. `SaxLvEpi` vs `SaxLvOpenEpi`)
 *
 * `presentLabels` are the labels that currently have at least one contour.
 */
export function canAddMyocardialContour(
  presentLabels: ContourLabel[],
  candidate: ContourLabel,
): CanAddVerdict {
  const role = getContourRole(candidate);
  if (role !== 'endo' && role !== 'epi') return { ok: true };

  const candidatePlane = getContourPlane(candidate);

  for (const present of presentLabels) {
    if (present === candidate) continue;

    const presentRole = getContourRole(present);
    if (presentRole !== 'endo' && presentRole !== 'epi') continue;

    if (getContourPlane(present) !== candidatePlane) {
      return {
        ok: false,
        reason: `Cannot mix ${getContourPlane(present)} and ${candidatePlane} contours on the same image.`,
      };
    }

    if (role === 'epi' && presentRole === 'epi') {
      return {
        ok: false,
        reason: `An epicardial contour (${present}) of a different type already exists.`,
      };
    }
  }

  return { ok: true };
}

/**
 * Enforces spatial relationships between contours on a single image:
 *  - the endocardial contour is clipped to stay inside the epicardial contour,
 *  - an epi drawn inside / cutting into the endo is discarded,
 *  - papillary / exclusion contours are clipped inside their endo and made
 *    non-crossing with their siblings.
 *
 * All adjustments operate in scaled (image) coordinates and only touch the
 * contour currently being added / modified. Only closed contours are handled;
 * open contours (LAX, open epi) are skipped so their behaviour is unchanged.
 */
export class ContourRules {
  constructor(private readonly store: ContourStore) {}

  /**
   * Returns `false` (and shows a warning) when adding `label` would conflict
   * with an existing endo/epi contour of an incompatible type.
   */
  public canAddLabel(label: ContourLabel): boolean {
    const role = getContourRole(label);
    if (role !== 'endo' && role !== 'epi') return true;

    const presentLabels = Object.entries(this.store.getDrawingContours())
      .filter(([, ids]) => Object.keys(ids).length > 0)
      .map(([presentLabel]) => presentLabel as ContourLabel);

    const verdict = canAddMyocardialContour(presentLabels, label);
    if (!verdict.ok) {
      useAlert().warning(
        verdict.reason ?? 'Contour type is not allowed on this image.',
      );
      return false;
    }
    return true;
  }

  /**
   * Gate for a user edit (add / modify / translate) before it is committed.
   * Returns `false` when the drawing must be discarded:
   *  - an epi drawn inside / cutting into the endo,
   *  - a papillary / exclusion contour without a parent endo, or drawn
   *    entirely outside its endo (nothing else is allowed for those).
   * `polygonScaled` is the candidate polygon in scaled (image) coordinates.
   */
  public isEditAllowed(contourId: ContourId, polygonScaled: PointArray): boolean {
    if (contourId.label === ContourLabel.MaskMeasurement) {
      return this.clipMaskToMyocardium(polygonScaled) !== null;
    }
    // LAX epi drawn inside the endo => discard; otherwise it is just adjusted
    if (contourId.label === ContourLabel.LaxLvEpi) {
      return this.isLaxEpiEditAllowed(polygonScaled);
    }
    // LAX endo drawn around the epi => discard (inverted hierarchy), matching
    // the SAX behaviour; minor crossings are just clamped by enforce
    if (contourId.label === ContourLabel.LaxLvEndo) {
      return this.isLaxEndoEditAllowed(polygonScaled);
    }
    const role = getContourRole(contourId.label);
    if (role === 'epi') return this.isEpiEditAllowed(contourId, polygonScaled);
    if (role === 'endo') return this.isEndoEditAllowed(contourId, polygonScaled);
    if (role === 'papillary' || role === 'exclusion') {
      return this.isInnerEditAllowed(contourId, polygonScaled);
    }
    return true;
  }

  /**
   * A papillary / exclusion contour must live inside its endo. The edit is
   * rejected when no parent endo is present or the drawing does not intersect
   * the endo at all (fully outside). Partial overlaps are allowed and clipped
   * inside by `enforce`.
   */
  private isInnerEditAllowed(
    innerId: ContourId,
    innerScaled: PointArray,
  ): boolean {
    const endoLabel = getEndoParent(innerId.label);
    if (!endoLabel || !CLOSED_LABELS.has(endoLabel)) return true;

    const endoId = { label: endoLabel, id: encodeContourLabelItemId([0]) };
    if (!this.store.isPresentContourId(endoId)) return false;

    const endo = this.store.getContour(endoId)?.getPolygonScaled();
    if (!endo) return false;

    return clampContourWithinParent(innerScaled, endo) !== null;
  }

  /**
   * Restricts a mask-measurement contour to the myocardium (inside the LV epi,
   * outside the LV endo) with no gap. Nothing is changed when there is no epi
   * to restrict against.
   */
  /**
   * Clamps the open LAX endo so it stays inside the LAX epi with a gap, while
   * still allowing it to extend past the epi at the open end of the contour.
   * Does nothing when either contour is missing.
   */
  private clampLaxEndoWithinEpi(endoId: ContourId): void {
    const endo = this.store.getContour(endoId);
    if (!endo) return;

    const epiId = {
      label: ContourLabel.LaxLvEpi,
      id: encodeContourLabelItemId([0]),
    };
    if (!this.store.isPresentContourId(epiId)) return;

    const epi = this.store.getContour(epiId)?.getPolygonScaled();
    if (!epi) return;

    const endoScaled = endo.getPolygonScaled();
    const clipped = clampOpenEndoWithinEpi(endoScaled, epi);
    if (!clipped || clipped === endoScaled) return;

    this.applyAdjustedOpenPolygon(endo, clipped);
  }

  private restrictMaskToMyocardium(maskId: ContourId): void {
    const mask = this.store.getContour(maskId);
    if (!mask) return;

    const maskScaled = mask.getPolygonScaled();
    const clip = this.clipMaskToMyocardium(maskScaled);
    if (clip && clip !== maskScaled) {
      this.applyAdjustedPolygon(mask, clip);
    }
  }

  /**
   * Computes the mask polygon restricted to the myocardium (inside LV epi,
   * outside LV endo), using no gap so the mask may touch either border.
   *
   * Returns:
   *  - the input reference unchanged when there is no LV epi (nothing to clip),
   *  - the clipped polygon when it lies (partly) in the myocardium,
   *  - `null` when the mask is entirely outside the myocardium (discard).
   */
  private clipMaskToMyocardium(maskScaled: PointArray): PointArray | null {
    const epiId = { label: ContourLabel.SaxLvEpi, id: encodeContourLabelItemId([0]) };
    if (this.store.isPresentContourId(epiId)) {
      const epi = this.store.getContour(epiId)?.getPolygonScaled();
      if (!epi) return maskScaled;

      // inside the epi (no gap)
      const inEpi = clampContourWithinParent(maskScaled, epi, 0);
      if (!inEpi) return null;

      // outside the endo (no gap): subtract every LV endo variant
      const endos = this.collectPolygons([
        ContourLabel.SaxLvEndo,
        ContourLabel.SaxLvEndoTrabecular,
      ]);
      const result = subtractSiblingOverlaps(inEpi, endos);
      if (result.length < 4) return null;

      return result;
    }

    // LAX: adjust the mask to the band between the open endo/epi lines (no
    // gap). The mask is never discarded here — if it does not overlap the band
    // it is simply left unchanged.
    const laxRegion = this.laxMyocardiumScaled();
    if (laxRegion) {
      const clipped = getPolygonIntersection(maskScaled, laxRegion);
      return clipped.length >= 4 ? clipped : maskScaled;
    }

    return maskScaled;
  }

  /**
   * Builds the LAX myocardial band (between the open endo and epi lines) in
   * scaled (image) coordinates, or `null` when either contour is missing.
   */
  private laxMyocardiumScaled(): PointArray | null {
    const endoId = {
      label: ContourLabel.LaxLvEndo,
      id: encodeContourLabelItemId([0]),
    };
    const epiId = {
      label: ContourLabel.LaxLvEpi,
      id: encodeContourLabelItemId([0]),
    };
    if (
      !this.store.isPresentContourId(endoId)
      || !this.store.isPresentContourId(epiId)
    ) {
      return null;
    }

    const endo = this.store.getContour(endoId) as PolygonOpenContour | undefined;
    const epi = this.store.getContour(epiId) as PolygonOpenContour | undefined;
    if (!endo || !epi) return null;

    // createLaxMyocardialPolygon works in internal (sub-pixel) coordinates;
    // convert to scaled (image) coordinates to match the mask.
    const polygon = createLaxMyocardialPolygon(endo, epi);
    if (polygon.length < 4) return null;

    const sub = ContourBase.subMatrix;
    return polygon.map(point => ({ x: point.x / sub, y: point.y / sub }));
  }

  /**
   * Returns `false` when an epi contour was drawn inside or cutting into the
   * endo, signalling that the drawing should be discarded (done silently; the
   * caller restores the previous polygon).
   * `epiScaled` is the candidate epi polygon in scaled (image) coordinates.
   */
  /**
   * LAX (open) epi: the edit is discarded only when the epi is drawn inside the
   * endo (inverted hierarchy). Any other overlap is just adjusted by `enforce`.
   */
  private isLaxEpiEditAllowed(epiScaled: PointArray): boolean {
    const endoId = {
      label: ContourLabel.LaxLvEndo,
      id: encodeContourLabelItemId([0]),
    };
    if (!this.store.isPresentContourId(endoId)) return true;

    const endo = this.store.getContour(endoId)?.getPolygonScaled();
    if (!endo) return true;

    return !isOpenEpiInsideEndo(epiScaled, endo);
  }

  /**
   * LAX (open) endo: the edit is discarded only when it is drawn around the epi
   * (inverted hierarchy — the epi ends up inside the endo). This mirrors the
   * SAX rule that an endo enclosing its epi is rejected. Any lesser overlap is
   * just clamped inside the epi by `enforce`.
   */
  private isLaxEndoEditAllowed(endoScaled: PointArray): boolean {
    const epiId = {
      label: ContourLabel.LaxLvEpi,
      id: encodeContourLabelItemId([0]),
    };
    if (!this.store.isPresentContourId(epiId)) return true;

    const epi = this.store.getContour(epiId)?.getPolygonScaled();
    if (!epi) return true;

    return !isOpenEpiInsideEndo(epi, endoScaled);
  }

  /**
   * Returns `false` when a (closed, SAX) endo was drawn entirely around its epi
   * (inverted hierarchy — the epi lies inside the endo), so the drawing is
   * silently discarded (restored by the caller). This mirrors the epi rule that
   * an epi drawn inside its endo is rejected. Partial crossings are kept and
   * clamped inside the epi by `enforce`.
   */
  private isEndoEditAllowed(endoId: ContourId, endoScaled: PointArray): boolean {
    if (getContourRole(endoId.label) !== 'endo') return true;
    if (!CLOSED_LABELS.has(endoId.label)) return true;

    for (const epiLabel of getEpiParentCandidates(endoId.label)) {
      if (!CLOSED_LABELS.has(epiLabel)) continue;

      const epiId = { label: epiLabel, id: encodeContourLabelItemId([0]) };
      if (!this.store.isPresentContourId(epiId)) continue;

      const epi = this.store.getContour(epiId)?.getPolygonScaled();
      if (epi && isPolygonInside(epi, endoScaled)) {
        // the endo encloses the epi => discard the drawing
        return false;
      }
    }
    return true;
  }

  private isEpiEditAllowed(epiId: ContourId, epiScaled: PointArray): boolean {
    if (getContourRole(epiId.label) !== 'epi') return true;
    if (!CLOSED_LABELS.has(epiId.label)) return true;

    for (const endoLabel of ENDO_LABELS) {
      if (!CLOSED_LABELS.has(endoLabel)) continue;
      if (!getEpiParentCandidates(endoLabel).includes(epiId.label)) continue;

      const endoId = { label: endoLabel, id: encodeContourLabelItemId([0]) };
      if (!this.store.isPresentContourId(endoId)) continue;

      const endo = this.store.getContour(endoId)?.getPolygonScaled();
      if (endo && !epiContainsEndo(epiScaled, endo)) {
        // silently discard the drawing (restored by the caller)
        return false;
      }
    }
    return true;
  }

  /**
   * Adjusts the just-committed contour so the hierarchy holds. Only the
   * contour identified by `contourId` is modified.
   */
  public enforce(contourId: ContourId): void {
    if (contourId.label === ContourLabel.MaskMeasurement) {
      this.restrictMaskToMyocardium(contourId);
      return;
    }

    // open LAX contours: keep the endo inside the epi (with a gap) except at
    // the open end of the contour
    if (contourId.label === ContourLabel.LaxLvEndo) {
      this.clampLaxEndoWithinEpi(contourId);
      // re-adjust dependents (e.g. mask measurement) to the new lines
      this.enforceDependents();
      return;
    }
    if (contourId.label === ContourLabel.LaxLvEpi) {
      this.clampLaxEndoWithinEpi({
        label: ContourLabel.LaxLvEndo,
        id: encodeContourLabelItemId([0]),
      });
      // re-adjust dependents (e.g. mask measurement) to the new lines
      this.enforceDependents();
      return;
    }

    const role = getContourRole(contourId.label);
    if (!role) return;
    if (!CLOSED_LABELS.has(contourId.label)) return;

    switch (role) {
      case 'endo':
        this.clampEndoWithinEpi(contourId);
        // the RV endo abuts the LV epi: no overlap, but touching is allowed
        if (contourId.label === ContourLabel.SaxRvEndo) {
          this.subtractLabelsFrom(contourId, [ContourLabel.SaxLvEpi]);
        }
        // re-adjust dependents drawn before this endo
        this.enforceDependents();
        break;
      case 'epi':
        // a degenerate epi (inside the endo) is rejected via isEpiEditAllowed;
        // otherwise re-clip the endo so endo and epi never cross.
        this.clampEndosWithinEpi(contourId);
        // keep the RV endo out of the (edited) LV epi
        if (contourId.label === ContourLabel.SaxLvEpi) {
          this.subtractFromLabels(contourId, [ContourLabel.SaxRvEndo]);
        }
        // re-adjust dependents drawn before this epi
        this.enforceDependents();
        break;
      case 'papillary':
        this.clampWithinEndo(contourId);
        this.subtractSiblings(contourId);
        // carve this papillary out of any overlapping exclusion contours
        this.subtractFromLabels(contourId, EXCLUSION_LABELS);
        break;
      case 'exclusion':
        this.clampWithinEndo(contourId);
        this.subtractSiblings(contourId);
        // an exclusion must not overlap papillary contours
        this.subtractLabelsFrom(contourId, PAPILLARY_LABELS);
        break;
      default:
        break;
    }
  }

  /**
   * Re-applies the rules to every contour whose geometry depends on the endo /
   * epi (papillary, exclusion and mask-measurement contours). Used after an
   * endo or epi edit so contours drawn earlier are brought back into line.
   *
   * These dependents never adjust the endo / epi themselves, so re-entering
   * `enforce` here cannot cascade back.
   */
  private enforceDependents(): void {
    const dependentLabels: ContourLabel[] = [
      ...PAPILLARY_LABELS,
      ...EXCLUSION_LABELS,
      ContourLabel.MaskMeasurement,
    ];

    const drawing = this.store.getDrawingContours();
    for (const label of dependentLabels) {
      const group = drawing[label];
      if (!group) continue;
      for (const id of Object.keys(group)) {
        this.enforce({ label, id: id as ContourLabelItemId });
      }
    }
  }

  private clampEndoWithinEpi(endoId: ContourId): void {
    for (const epiLabel of getEpiParentCandidates(endoId.label)) {
      if (!CLOSED_LABELS.has(epiLabel)) continue;
      const epiId = { label: epiLabel, id: encodeContourLabelItemId([0]) };
      if (this.store.isPresentContourId(epiId)) {
        this.clampInside(endoId, epiId);
        return;
      }
    }
  }

  private clampEndosWithinEpi(epiId: ContourId): void {
    for (const endoLabel of ENDO_LABELS) {
      if (!CLOSED_LABELS.has(endoLabel)) continue;
      if (!getEpiParentCandidates(endoLabel).includes(epiId.label)) continue;

      const endoId = { label: endoLabel, id: encodeContourLabelItemId([0]) };
      if (this.store.isPresentContourId(endoId)) {
        this.clampInside(endoId, epiId);
      }
    }
  }

  private clampWithinEndo(innerId: ContourId): void {
    const endoLabel = getEndoParent(innerId.label);
    if (!endoLabel || !CLOSED_LABELS.has(endoLabel)) return;

    const endoId = { label: endoLabel, id: encodeContourLabelItemId([0]) };
    if (this.store.isPresentContourId(endoId)) {
      this.clampInside(innerId, endoId);
    }
  }

  private clampInside(innerId: ContourId, outerId: ContourId): void {
    const inner = this.store.getContour(innerId);
    const outer = this.store.getContour(outerId);
    if (!inner || !outer) return;

    const innerScaled = inner.getPolygonScaled();
    const clipped = clampContourWithinParent(
      innerScaled,
      outer.getPolygonScaled(),
    );
    if (!clipped) return;
    // Already inside: clampContourWithinParent returns the same reference.
    if (clipped === innerScaled) return;

    this.applyAdjustedPolygon(inner, clipped);
  }

  private subtractSiblings(contourId: ContourId): void {
    const target = this.store.getContour(contourId);
    if (!target) return;

    const group = this.store.getDrawingContours()[contourId.label];
    if (!group) return;

    const siblings: PointArray[] = [];
    for (const id of Object.keys(group)) {
      if (id === contourId.id) continue;
      const sibling = this.store.getContour({
        label: contourId.label,
        id: id as ContourLabelItemId,
      });
      if (sibling) siblings.push(sibling.getPolygonScaled());
    }
    if (siblings.length === 0) return;

    const targetScaled = target.getPolygonScaled();
    const result = subtractSiblingOverlaps(targetScaled, siblings);
    // No overlap: subtractSiblingOverlaps returns the same reference.
    if (result === targetScaled || result.length < 4) return;

    this.applyAdjustedPolygon(target, result);
  }

  /** Collects every contour polygon (scaled) for the given labels. */
  private collectPolygons(labels: ContourLabel[]): PointArray[] {
    const polygons: PointArray[] = [];
    const drawing = this.store.getDrawingContours();
    for (const label of labels) {
      const group = drawing[label];
      if (!group) continue;
      for (const id of Object.keys(group)) {
        const contour = this.store.getContour({
          label,
          id: id as ContourLabelItemId,
        });
        if (contour) polygons.push(contour.getPolygonScaled());
      }
    }
    return polygons;
  }

  /** Subtracts every contour of `labels` from the target contour. */
  private subtractLabelsFrom(targetId: ContourId, labels: ContourLabel[]): void {
    const target = this.store.getContour(targetId);
    if (!target) return;

    const others = this.collectPolygons(labels);
    if (others.length === 0) return;

    const targetScaled = target.getPolygonScaled();
    const result = subtractSiblingOverlaps(targetScaled, others);
    if (result === targetScaled || result.length < 4) return;

    this.applyAdjustedPolygon(target, result);
  }

  /** Subtracts the edited contour from every contour of `labels`. */
  private subtractFromLabels(sourceId: ContourId, labels: ContourLabel[]): void {
    const source = this.store.getContour(sourceId)?.getPolygonScaled();
    if (!source || source.length < 4) return;

    const drawing = this.store.getDrawingContours();
    for (const label of labels) {
      const group = drawing[label];
      if (!group) continue;
      for (const id of Object.keys(group)) {
        const targetId = { label, id: id as ContourLabelItemId };
        const target = this.store.getContour(targetId);
        if (!target) continue;

        const targetScaled = target.getPolygonScaled();
        const result = subtractSiblingOverlaps(targetScaled, [source]);
        if (result === targetScaled || result.length < 4) continue;

        this.applyAdjustedPolygon(target, result);
      }
    }
  }

  /**
   * Applies an adjusted polygon to a contour, guaranteeing the result has no
   * loops and consecutive points on the sub-pixel matrix.
   *
   * The turf output is first de-duplicated and unkinked, then handed to
   * `setPolygonScaled`, which grid-aligns, interpolates to consecutive
   * sub-pixel points, removes loops and validates. If the processed contour is
   * not valid, the previous polygon is restored so an adjustment never
   * destroys a contour.
   */
  private applyAdjustedPolygon(contour: ContourBase, polygon: PointArray): void {
    if (polygon.length < 4) return;

    const previous = contour.getPolygon().map(point => ({ ...point }));
    contour.setPolygonScaled(this.cleanAdjustedPolygon(polygon));

    if (!(contour as PolygonClosedContour).isValid()) {
      contour.setPolygon(previous);
    }
  }

  /**
   * Applies an adjusted polyline to an open contour. `setPolygonScaled` already
   * interpolates to consecutive sub-pixel points, removes loops (open-aware)
   * and validates; the previous polyline is restored if the result is invalid.
   */
  private applyAdjustedOpenPolygon(
    contour: ContourBase,
    polygon: PointArray,
  ): void {
    if (polygon.length < 2) return;

    const previous = contour.getPolygon().map(point => ({ ...point }));
    contour.setPolygonScaled(polygon);

    if (!(contour as PolygonOpenContour).isValid()) {
      contour.setPolygon(previous);
    }
  }

  private cleanAdjustedPolygon(polygon: PointArray): PointArray {
    try {
      const deduped = removeDuplicatePoints(polygon);
      if (deduped.length < 3) return polygon;
      deduped.push({ x: deduped[0].x, y: deduped[0].y });
      return removeLoops(deduped);
    }
    catch {
      return polygon;
    }
  }
}
